import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";
import { useEnrollments } from "./useEnrollments";
import { useStudentCourse } from "./useStudentCourse";
import { useQuery } from "@tanstack/react-query";
import { Enrollment } from "@/types/enrollment";

export const useCreatorEmail = () => {
  const [creatorEmail, setCreatorEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { enrollments } = useEnrollments();
  const location = useLocation();
  
  // Check if we're on a course page
  const courseIdFromURL = location.pathname.includes('/course/') 
    ? location.pathname.split('/course/')[1]?.split('/')[0] 
    : null;
  
  // If we're on a course page, use that course ID
  const { courseData: currentCourseData } = useStudentCourse(courseIdFromURL);

  useEffect(() => {
    const getCreatorEmail = async () => {
      try {
        setIsLoading(true);
        
        // Strategy 1: Try to get email from current course page
        if (courseIdFromURL && currentCourseData) {
          // Check for contact_email on the course
          if (currentCourseData.contact_email) {
            setCreatorEmail(currentCourseData.contact_email);
            return;
          }
          
          // Fall back to creator's profile email
          if (currentCourseData.creator_id) {
            const { data: creatorData, error: creatorError } = await supabase
              .from('profiles')
              .select('email')
              .eq('id', currentCourseData.creator_id)
              .single();
              
            if (!creatorError && creatorData?.email) {
              setCreatorEmail(creatorData.email);
              return;
            }
          }
        }
        
        // Strategy 2: Try to get email from enrollments
        const enrollmentsArray = enrollments as Enrollment[] || [];
        if (enrollmentsArray && enrollmentsArray.length > 0) {
          const sortedEnrollments = [...enrollmentsArray].sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          
          const mostRecentEnrollment = sortedEnrollments[0];
          
          if (!mostRecentEnrollment?.course_id) {
            return;
          }
          
          // Check for contact_email on the course
          const { data: courseData, error: courseError } = await supabase
            .from('courses')
            .select('contact_email, creator_id')
            .eq('id', mostRecentEnrollment.course_id)
            .single();
            
          if (!courseError && courseData && courseData.contact_email) {
            setCreatorEmail(courseData.contact_email);
            return;
          }
          
          // Fall back to creator's profile email
          if (courseData && courseData.creator_id) {
            const { data: creatorData, error: creatorError } = await supabase
              .from('profiles')
              .select('email')
              .eq('id', courseData.creator_id)
              .single();
              
            if (!creatorError && creatorData?.email) {
              setCreatorEmail(creatorData.email);
              return;
            }
          }
        }
        
        // No fallback email - it should just be null
        setCreatorEmail(null);
        
      } catch (error) {
        console.error("Error getting creator email:", error);
        setCreatorEmail(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    getCreatorEmail();
  }, [courseIdFromURL, currentCourseData, enrollments]);

  return {
    creatorEmail,
    isLoading
  };
};
